import {Button, FormControl, Grid, Stack} from "@mui/material";
import {useTranslation} from "react-i18next";
import {Organization} from "../../../../app/api/model/organization";
import StageSelect, {NO_SELECTION} from "../../../../components/input/StageSelect";
import {useFormik} from "formik";
import {OrganizationCreateFromOrderRequest} from "../../../../app/api/types";
import {useOrganizationCreateFromOrderMutation} from "../../../../app/api/api";
import React from "react";
import LabeledValue from "../../../../components/Layout/Page/LabeledValue";
import {useSnackbar} from "notistack";
import * as yup from "yup";
import {getFormErrorText} from "../../../../utils/getFormTextError";
import DefaultDialog, {DefaultDialogSize} from "../../../../components/Dialog/ClosableDialog";
import {UseModal} from "../../../../hooks/useModal";

interface OrganizationCreateFromProps {
    orderId: number;
    organization?: Organization;
    modal: UseModal;
}

export default function OrganizationCreateFromOrder({orderId, organization, modal}: OrganizationCreateFromProps) {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();

    const [createOrganizationFromOrder] = useOrganizationCreateFromOrderMutation();

    const form = useFormik({
        initialValues: {
            name: organization?.name || "",
            adminEmail: organization?.adminEmail || "",
            stageIdentifier: organization?.productiveStage || NO_SELECTION,
        },
        validateOnChange: true,
        validationSchema: yup.object({
            stageIdentifier: yup.string().test({
                name: "stageValidationError",
                message: t("validation_primaryStageMustBeSelected"),
                test: function (value) {
                    return value !== NO_SELECTION;
                },
            }),
        }),
        onSubmit: async (values, submitProps) => {
            const requestData = {
                orderId,
                stageIdentifier: values.stageIdentifier,
            } as OrganizationCreateFromOrderRequest;

            await createOrganizationFromOrder(requestData).unwrap();
            enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});
            modal.setIsOpen(false);
            submitProps.resetForm({values});
        },
    });

    const handleSubmit = () => {
        form.handleSubmit();
    };

    return (
        <DefaultDialog
            size={DefaultDialogSize.LARGE}
            open={modal.isOpen}
            titleKey={"action_createOrganization"}
            onClose={modal.onClose}
            actions={
                <Button color="primary" variant="contained" type="submit" onClick={handleSubmit}>
                    {t("action_create")}
                </Button>
            }
        >
            <Stack>
                <form onSubmit={form.handleSubmit}>
                    <Grid container xs={12} sx={{marginBottom: "24px"}}>
                        <LabeledValue label={t("organizations_name")} content={form.values.name} />
                        <LabeledValue label={t("organizations_adminEmail")} content={form.values.adminEmail} />
                    </Grid>
                    <FormControl fullWidth error={getFormErrorText(form, ["stageIdentifier"])}>
                        <StageSelect
                            label={t("organizations_productiveStage")}
                            fieldName={"stageIdentifier"}
                            form={form}
                            isGridItem={true}
                            gridProps={{xs: 12}}
                            error={!!getFormErrorText(form, "stageIdentifier")}
                            requestParams={{onlyProductive: true, showTotal: true}}
                            t={t}
                        />
                    </FormControl>
                </form>
            </Stack>
        </DefaultDialog>
    );
}
